import api from '@/services/api'
import type { BillingCycle, TierKey } from './pricing'

export const RAZORPAY_SCRIPT_ID = 'razorpay-checkout-js'
const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js'

let loading: Promise<void> | null = null

/** Loads checkout.js once, and only when a Pay button asks for it. */
export function loadRazorpay(): Promise<void> {
  if (window.Razorpay) return Promise.resolve()
  if (loading) return loading
  loading = new Promise<void>((resolve, reject) => {
    let script = document.getElementById(RAZORPAY_SCRIPT_ID) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = RAZORPAY_SCRIPT_ID
      script.src = RAZORPAY_SRC
      script.async = true
      document.body.appendChild(script)
    }
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => {
      loading = null
      script?.remove()
      reject(new Error('The payment window could not be loaded. Check your connection and try again.'))
    })
  })
  return loading
}

export interface CheckoutSession {
  subscription_id: string
  key_id?: string
  plan_name?: string
  user_name?: string
  user_email?: string
}

export type CheckoutOutcome =
  | { kind: 'paid'; message: string }
  | { kind: 'admin'; message: string }
  | { kind: 'dismissed' }

/** Opens Razorpay for an already-created subscription and verifies the payment on success. */
export async function openCheckout(session: CheckoutSession, tier: TierKey, cycle: BillingCycle): Promise<CheckoutOutcome> {
  await loadRazorpay()
  return new Promise<CheckoutOutcome>((resolve, reject) => {
    const razorpay = new window.Razorpay({
      key: session.key_id ?? '',
      subscription_id: session.subscription_id,
      name: 'Zupa',
      description: session.plan_name,
      prefill: { name: session.user_name, email: session.user_email },
      handler: (response) => {
        if (!('razorpay_subscription_id' in response)) {
          reject(new Error('The payment provider sent an unexpected response.'))
          return
        }
        api
          .verifySubscription({
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            tier,
            billing_cycle: cycle,
          })
          .then((result) => {
            if (result.success) resolve({ kind: 'paid', message: result.message })
            else reject(new Error(result.message || 'We could not confirm the payment.'))
          }, reject)
      },
      modal: { ondismiss: () => resolve({ kind: 'dismissed' }) },
    })
    razorpay.on('payment.failed', () => reject(new Error('The payment did not go through. You have not been charged.')))
    razorpay.open()
  })
}

/** Pay for a plan: create the subscription on the server, then open checkout for it. */
export async function payForPlan(tier: TierKey, cycle: BillingCycle, coupon?: string): Promise<CheckoutOutcome> {
  await loadRazorpay()
  const session = await api.createSubscription(tier, cycle, coupon || undefined)
  if (session.subscription_id === 'admin_free_access') {
    return { kind: 'admin', message: `Your plan is now ${session.plan_name}.` }
  }
  return openCheckout(session, tier, cycle)
}
